import { create } from "zustand";

export type ChannelType = "party" | "match";

export type ChannelMeta = {
  channelId: string | null;
  matchId?: string;
  matchStatus?: string;
  memberCount?: number;
};

export type ChatChannel = {
  id: string;
  type: ChannelType;
  title: string;
  unreadCount: number;
  meta: ChannelMeta;
};

export type PopupState = "open" | "minimized" | "closed";

type ChatPopupStore = {
  channels: ChatChannel[];
  activeChannelId: string | null;
  state: PopupState;
  expanded: boolean;

  addChannel: (channel: Omit<ChatChannel, "unreadCount">) => void;
  registerChannel: (channel: Omit<ChatChannel, "unreadCount">) => void;
  removeChannel: (id: string) => void;
  setActiveChannel: (id: string) => void;
  minimize: () => void;
  expand: () => void;
  close: () => void;
  toggleExpanded: () => void;
  updateChannelUnread: (id: string, count: number) => void;
  updateChannelMeta: (id: string, meta: Partial<ChannelMeta>) => void;
};

export const useChatPopupStore = create<ChatPopupStore>((set) => ({
  channels: [],
  activeChannelId: null,
  state: "closed",
  expanded: false,

  addChannel: (channel) =>
    set((s) => {
      const existing = s.channels.find((c) => c.id === channel.id);
      if (existing) {
        return {
          activeChannelId: channel.id,
          state: "open",
          channels: s.channels.map((c) =>
            c.id === channel.id
              ? { ...c, title: channel.title, meta: { ...c.meta, ...channel.meta } }
              : c,
          ),
        };
      }
      return {
        channels: [...s.channels, { ...channel, unreadCount: 0 }],
        activeChannelId: channel.id,
        state: "open",
      };
    }),

  registerChannel: (channel) =>
    set((s) => {
      const existing = s.channels.find((c) => c.id === channel.id);
      if (existing) {
        return {
          channels: s.channels.map((c) =>
            c.id === channel.id
              ? { ...c, title: channel.title, meta: { ...c.meta, ...channel.meta } }
              : c,
          ),
        };
      }
      const newState = s.state === "closed" ? "minimized" : s.state;
      return {
        channels: [...s.channels, { ...channel, unreadCount: 0 }],
        activeChannelId: s.activeChannelId ?? channel.id,
        state: newState,
      };
    }),

  removeChannel: (id) =>
    set((s) => {
      const channels = s.channels.filter((c) => c.id !== id);
      if (channels.length === 0) {
        return { channels, activeChannelId: null, state: "closed" };
      }
      const activeChannelId = s.activeChannelId === id ? channels[0].id : s.activeChannelId;
      return { channels, activeChannelId };
    }),

  setActiveChannel: (id) => set(() => ({ activeChannelId: id })),

  minimize: () => set(() => ({ state: "minimized" })),

  expand: () => set(() => ({ state: "open" })),

  close: () => set(() => ({ state: "closed" })),

  toggleExpanded: () => set((s) => ({ expanded: !s.expanded })),

  updateChannelUnread: (id, count) =>
    set((s) => ({
      channels: s.channels.map((c) => (c.id === id ? { ...c, unreadCount: count } : c)),
    })),

  updateChannelMeta: (id, meta) =>
    set((s) => ({
      channels: s.channels.map((c) => (c.id === id ? { ...c, meta: { ...c.meta, ...meta } } : c)),
    })),
}));
